import { ApiResponse } from '@/shared/types';
import { axiosClient } from '@/shared/apis/config';
import { useQuery } from '@tanstack/react-query';
import { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { z } from 'zod';
import {
  FeatureLimitEnumMapSchema,
  FeatureNumericTypeSchema,
  FeatureToggleType,
  PointItemTypeSchema,
  ProductBillingCycleType,
  ProductDiscountType,
} from '@/shared/constants';
import { productBillingSchema } from '@/shared/billing';

export const getProductPlansResponseSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    billingCycleType: z.nativeEnum(ProductBillingCycleType),
    // 서버가 요청 리전의 통화 하나만 내려준다. 아래 금액은 이 통화의 최소 단위(KRW=원, USD=센트)이며
    // 표시 금액이자 청구될 금액이다 — 환산하지 않는다.
    currency: z.string(),
    customerAmount: z.number(),
    originAmount: z.number(),
    discountType: z.nativeEnum(ProductDiscountType),
    discountValue: z.number(),
    discountAmount: z.number(),

    pointMap: z.record(PointItemTypeSchema, z.number()),
    featureToggles: z.array(z.nativeEnum(FeatureToggleType)),
    featureLimitNumericMap: z.record(FeatureNumericTypeSchema, z.number()),
    featureLimitEnumMap: FeatureLimitEnumMapSchema,

    isPopular: z.boolean(),
    seq: z.number().optional(), // 플랜 우선순위 (높을수록 상위 플랜)
  })
  .merge(productBillingSchema);

export type GetProductPlansResponseType = z.infer<
  typeof getProductPlansResponseSchema
>;

export type getProductPlansResponse = ApiResponse<
  GetProductPlansResponseType[]
>;

export const getProductPlansApi = async (
  billingCycleType: ProductBillingCycleType,
  spaceId?: number,
) => {
  try {
    const response = await axiosClient.get<getProductPlansResponse>(
      '/v1.0/product/plans',
      {
        params: {
          billingCycleType,
        },
        headers: {
          'X-Space-Id': spaceId,
        },
      },
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const QueryKeyUseGetProductPlansApi = 'getProductPlans' as const;

export const useGetProductPlansApi = (
  {
    spaceId,
    billingCycleType,
  }: { spaceId?: number; billingCycleType: ProductBillingCycleType },
  options?: CustomQueryOptions<getProductPlansResponse>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [
      QueryKeyUseGetProductPlansApi,
      billingCycleType,
      spaceId,
      ...(queryKey ?? []),
    ],
    queryFn: async () => await getProductPlansApi(billingCycleType, spaceId),
    ...rest,
  });
};
